import { Link } from "react-router-dom";
import { QrCode, FileCheck2, Microscope, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

export default function LabTestingSection() {
  return (
    <section className="py-24 bg-navy-900 text-white relative overflow-hidden" data-testid="lab-testing-home-section">
      <img
        src="https://images.unsplash.com/photo-1579165466991-467135ad3110?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="Laboratory testing"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-900/95 to-blue-950/80" />
      <div className="absolute -top-20 right-10 h-72 w-72 bg-blue-600/20 blur-3xl rounded-full" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-blue-400">Laboratory Testing</span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium font-display leading-tight">
            Every Batch. Independently Verified.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Our compounds undergo HPLC analysis and mass spectrometry verification through accredited third-party laboratories. Certificates of Analysis are generated for every batch and available upon request.
          </p>
          <Link to="/lab-testing">
            <Button size="lg" className="mt-8 rounded-full bg-white text-slate-900 hover:bg-blue-50 hover:text-blue-700 hover:shadow-blue-glow hover:-translate-y-0.5 transition-all duration-300" data-testid="lab-testing-cta-btn">
              View Laboratory Testing <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {[
            { icon: Microscope, title: "HPLC & Mass Spectrometry", desc: "Dual-method analytical verification of purity and structural identity." },
            { icon: FileCheck2, title: "Certificate of Analysis", desc: "Batch-specific COA documentation available." },
            { icon: QrCode, title: "QR Code Verification", desc: "Scan to instantly access batch-level testing data and documentation." },
          ].map((f, idx) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, x: 14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="glass-panel-dark rounded-2xl p-5 flex items-start gap-4 hover:border-blue-400/40 hover:shadow-blue-glow transition-all duration-300"
              data-testid={`lab-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="h-10 w-10 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                <f.icon className="h-5 w-5 text-blue-300" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display font-semibold text-sm text-white mb-1">{f.title}</h3>
                <p className="text-xs text-slate-300 leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
